import { useEffect, useState } from "react";
import { api, money, dateOnly, formatApiError } from "@/lib/api";
import { Wallet, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Payable() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        provider_name: "", concept: "", amount: 0, iva: 0,
        issue_date: new Date().toISOString().slice(0, 10),
        due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    });

    const load = async () => {
        try {
            const r = await api.get("/payables");
            if (r?.data) setList(r.data);
        } catch (e) { console.warn(e); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/payables", { ...form, amount: parseFloat(form.amount), iva: parseFloat(form.iva) || parseFloat(form.amount) * 0.16 });
            toast.success("Cuenta por pagar registrada");
            setOpen(false);
            setForm({ provider_name: "", concept: "", amount: 0, iva: 0, issue_date: new Date().toISOString().slice(0, 10), due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const pay = async (id) => {
        try { await api.patch(`/payables/${id}/pay`); toast.success("Pagada"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const [openStatus, setOpenStatus] = useState(null);
    const [statusValue, setStatusValue] = useState("pendiente");
    const [statusNote, setStatusNote] = useState("");
    const submitStatusChange = async () => {
        try {
            await api.patch(`/payables/${openStatus.id}/status`, { status: statusValue, note: statusNote });
            toast.success(`Estatus: ${statusValue}`);
            setOpenStatus(null);
            setStatusNote("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const total_pending = list.filter((p) => p.status === "pendiente").reduce((s, p) => s + (p.total || 0), 0);
    const total_overdue = list.filter((p) => p.days_overdue > 0).reduce((s, p) => s + (p.total || 0), 0);

    return (
        <div className="space-y-4" data-testid="payable-page">
            <div className="flex justify-between items-center">
                <div className="grid grid-cols-2 gap-3 flex-1 mr-4">
                    <div className="fm-card p-3"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Por pagar</div><div className="font-mono font-bold text-xl">{money(total_pending)}</div></div>
                    <div className="fm-card p-3 border-l-4 border-l-red-600"><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Vencido</div><div className="font-mono font-bold text-xl text-red-600">{money(total_overdue)}</div></div>
                </div>
                <button onClick={() => setOpen(true)} className="fm-btn-accent"><Plus className="w-3.5 h-3.5" /> Nueva cuenta</button>
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b border-slate-200">
                        {["Folio", "Proveedor", "Concepto", "Monto", "IVA", "Total", "Vencimiento", "Estatus", ""].map((h) => (
                            <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {list.map((p) => (
                            <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-mono text-xs">{p.folio}</td>
                                <td className="py-2 px-3 font-medium">{p.provider_name}</td>
                                <td className="py-2 px-3">{p.concept}</td>
                                <td className="py-2 px-3 text-right font-mono">{money(p.amount)}</td>
                                <td className="py-2 px-3 text-right font-mono text-slate-500">{money(p.iva)}</td>
                                <td className="py-2 px-3 text-right font-mono font-bold">{money(p.total)}</td>
                                <td className="py-2 px-3 text-xs font-mono">{dateOnly(p.due_date)} {p.days_overdue > 0 && <span className="text-red-600 ml-1">({p.days_overdue}d)</span>}</td>
                                <td className="py-2 px-3"><button onClick={() => { setOpenStatus(p); setStatusValue(p.status); }} className={`fm-badge cursor-pointer hover:opacity-80 ${p.status === "pagado" ? "fm-badge-positive" : p.status === "cancelado" ? "fm-badge-neutral" : p.status === "vencido" ? "fm-badge-critical" : "fm-badge-warning"}`} title="Click para cambiar estatus">{p.status}</button></td>
                                <td className="py-2 px-3">{p.status === "pendiente" && <button onClick={() => pay(p.id)} className="text-xs text-slate-700 underline hover:text-red-600">Pagar</button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {list.length === 0 && <div className="p-10 text-center text-sm text-slate-500"><Wallet className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />Sin cuentas por pagar.</div>}
            </div>

            {open && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-lg w-full">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold">Nueva cuenta por pagar</h3>
                            <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <div><label className="fm-label">Proveedor *</label><input required value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} className="fm-input" /></div>
                            <div><label className="fm-label">Concepto *</label><input required value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} className="fm-input" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="fm-label">Monto</label><input type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">IVA</label><input type="number" value={form.iva} onChange={(e) => setForm({ ...form, iva: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Emisión</label><input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Vencimiento</label><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="fm-input" /></div>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={() => setOpen(false)} className="fm-btn-outline">Cancelar</button>
                                <button type="submit" className="fm-btn-accent">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {openStatus && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-md w-full">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold">Cambiar estatus de cuenta por pagar</h3>
                            <button onClick={() => setOpenStatus(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-sm"><span className="text-slate-500">Folio:</span> <span className="font-mono font-bold">{openStatus.folio}</span> · {openStatus.provider_name}</div>
                            <div>
                                <label className="fm-label">Nuevo estatus</label>
                                <select value={statusValue} onChange={(e) => setStatusValue(e.target.value)} className="fm-input">
                                    <option value="pendiente">Pendiente</option>
                                    <option value="pagado">Pagado</option>
                                    <option value="vencido">Vencido</option>
                                    <option value="cancelado">Cancelado</option>
                                </select>
                            </div>
                            <div>
                                <label className="fm-label">Nota (opcional)</label>
                                <textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} className="fm-input" placeholder="Motivo o referencia del cambio" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={submitStatusChange} className="fm-btn-primary flex-1 justify-center">Guardar</button>
                                <button onClick={() => setOpenStatus(null)} className="fm-btn-outline">Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
